/// <amd-module name="Artifacts/Package/components/package-details-common/UpstreamOverrideModal" />
import { PackageActions, PackageModule } from "Artifacts/Package/redux";
import { IPackageDetailsModuleState } from "Artifacts/Package/redux/Contracts";
import * as Res from "Artifacts/Package/Resources";
import { DefaultLoader } from "Artifacts/Root/components/DefaultLoader";
import { ErrorBannerLoader } from "Artifacts/Root/components/ErrorBannerLoader";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { ComponentState } from "Artifacts/Root/Constants";
import { registerModal } from "Artifacts/Root/containers/ModalManager";
import { IProtocol } from "Artifacts/Root/protocols/types";
import { connectDynamicModules } from "Artifacts/Root/redux";
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";
import { ContentSize } from "VSSUI/Callout";
import { Dialog } from "VSSUI/Dialog";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Toggle } from "VSSUI/Toggle";

export interface IUpstreamOverrideModalProps {
  disabled: boolean;
  onDismiss: () => void;
  onOverrideToggle: (allowExternalVersions: boolean) => void;
  protocol: IProtocol;
  packageName: string;
}

const Actions = {
  upstreamOverrideModalStarted: PackageActions.upstreamOverrideModalStarted,
};

interface IMapStateToProps {
  componentState: ComponentState;
  overrideToggleComponentState: ComponentState;
  overrideStatus?: boolean;
}

export const UpstreamOverrideModalId = "UpstreamOverrideModal";

const UpstreamOverrideModal: React.FC<IUpstreamOverrideModalProps &
  IMapStateToProps &
  typeof Actions> = ({
  onDismiss,
  onOverrideToggle,
  overrideToggleComponentState,
  disabled,
  componentState,
  upstreamOverrideModalStarted,
  overrideStatus,
  protocol,
  packageName,
}) => {
  React.useEffect(() => {
    upstreamOverrideModalStarted(protocol, packageName);
  }, [packageName, protocol, upstreamOverrideModalStarted]);

  const [overrideState, setOverrideState] = useState<boolean>(false);

  // Set initial form state properly once we've loaded the params from the backend
  React.useEffect(() => {
    overrideStatus && setOverrideState(overrideStatus);
  }, [overrideStatus]);

  return (
    <Dialog
      className="upstream-override"
      titleProps={{ text: Res.UpstreamOverride }}
      onDismiss={onDismiss}
      contentSize={ContentSize.Medium}
      footerButtonProps={[
        {
          onClick: onDismiss,
          text: Res.Modal_Close,
        },
      ]}
    >
      <DefaultLoader state={componentState}>
        <ErrorBannerLoader state={overrideToggleComponentState} noLoadingSpinner>
          <MessageCard className="dialog-warning-card" severity={MessageCardSeverity.Warning}>
            {Res.UpstreamBehaviorAlert}
            <FWLink linkId="2152379">{Res.LearnMore}</FWLink>
          </MessageCard>
          <Toggle
            text={Res.UpstreamOverride_AllowButton}
            checked={overrideState}
            onChange={(_, value) => {
              setOverrideState(value);
              onOverrideToggle(value);
            }}
            disabled={disabled}
          />
        </ErrorBannerLoader>
      </DefaultLoader>
    </Dialog>
  );
};

const mapStateToProps = (state: IPackageDetailsModuleState): IMapStateToProps => {
  const rootUiState = state.packageState.ui;
  return {
    componentState: rootUiState.upstreamOverrideModal,
    overrideToggleComponentState: rootUiState.upstreamOverrideCommandState,
    overrideStatus: rootUiState.upstreamOverrideStatus,
  };
};

const ConnectedUpstreamOverrideModal = connectDynamicModules<IUpstreamOverrideModalProps>(
  connect<IMapStateToProps, typeof Actions, IUpstreamOverrideModalProps>(
    mapStateToProps,
    Actions,
  )(UpstreamOverrideModal),
  [PackageModule],
);

registerModal(UpstreamOverrideModalId, ConnectedUpstreamOverrideModal);

export { UpstreamOverrideModal };
